// ============================================
// PA Settings Page — Account Management
// ============================================

import React, { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import api from '../../services/api';
import Sidebar from '../../components/common/Sidebar';
import BottomNav from '../../components/common/BottomNav';
import DriverMobileHeader from '../../components/driver/DriverMobileHeader';
import { paMenuItems } from './paNav';
import { loadUser } from '../../redux/slices/authSlice';
import NotificationBell from '../../components/common/NotificationBell';
import { useTheme } from '../../context/ThemeContext';

const PASettings = () => {
    const dispatch = useDispatch();
    const { user } = useSelector((state) => state.auth);
    const { theme, toggleTheme } = useTheme();
    const [formData, setFormData] = useState({
        name: user?.name || '',
        phone: user?.phone || '',
    });
    const [loading, setLoading] = useState(false);
    const [isVerified, setIsVerified] = useState(user?.isVerified || false);
    const [message, setMessage] = useState({ type: '', text: '' });

    // Fetch fresh profile from API on mount to reconcile phone number
    const fetchProfile = useCallback(async () => {
        try {
            const res = await api.get('/pa/profile');
            const p = res.data.data;
            setFormData(prev => ({
                ...prev,
                name: p.name || prev.name,
                phone: p.phone || prev.phone,
            }));
            setIsVerified(p.isVerified || false);
        } catch (err) { console.error('Settings fetch profile error:', err); }
    }, []);

    useEffect(() => { fetchProfile(); }, [fetchProfile]);

    // Password State
    const [passwordData, setPasswordData] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
    });
    const [pwdLoading, setPwdLoading] = useState(false);
    const [pwdMessage, setPwdMessage] = useState({ type: '', text: '' });
    const [showPwd, setShowPwd] = useState(false);

    const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });
    const handlePasswordChange = (e) => setPasswordData({ ...passwordData, [e.target.name]: e.target.value });

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setMessage({ type: '', text: '' });
        try {
            await api.patch('/pa/profile', formData);
            setMessage({ type: 'success', text: 'Profile updated successfully!' });
            dispatch(loadUser());
        } catch (error) {
            setMessage({ type: 'error', text: error.response?.data?.message || 'Update failed' });
        } finally { setLoading(false); }
    };

    const handlePasswordSubmit = async (e) => {
        e.preventDefault();
        setPwdMessage({ type: '', text: '' });

        if (passwordData.newPassword !== passwordData.confirmPassword) {
            setPwdMessage({ type: 'error', text: 'New passwords do not match' });
            return;
        }

        setPwdLoading(true);
        try {
            await api.put('/auth/change-password', {
                currentPassword: passwordData.currentPassword,
                newPassword: passwordData.newPassword
            });
            setPwdMessage({ type: 'success', text: 'Password changed successfully' });
            setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
        } catch (error) {
            setPwdMessage({ type: 'error', text: error.response?.data?.message || 'Failed to change password' });
        } finally {
            setPwdLoading(false);
        }
    };

    const handleLogout = () => {
        localStorage.removeItem('pa_token');
        localStorage.removeItem('pa_refreshToken');
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
        window.location.href = '/login';
    };

    return (
        <div className="flex flex-col sm:flex-row h-screen bg-slate-50 dark:bg-[#050510] text-slate-900 dark:text-white font-sans transition-colors duration-500 aurora-bg relative pb-20 sm:pb-0">
            <Sidebar items={paMenuItems} theme="violet" title="IdaCab PA" />
            <div className="flex-1 flex flex-col overflow-hidden">
                <DriverMobileHeader
                    user={user}
                    theme={theme}
                    toggleTheme={toggleTheme}
                    settingsPath="/pa/settings"
                />

                <header className="hidden sm:flex h-24 bg-white/40 dark:bg-slate-900/40 backdrop-blur-3xl items-center justify-between px-10 flex-shrink-0 z-50 relative border-b border-white/60 dark:border-white/5 shadow-sm dark:shadow-2xl transition-colors duration-500">
                    <h2 className="text-2xl font-black bg-gradient-to-r from-violet-600 to-purple-400 bg-clip-text text-transparent tracking-tighter">Account Settings</h2>
                    <div className="flex items-center gap-3">
                        <NotificationBell />
                    </div>
                </header>

                <main className="flex-1 overflow-y-auto p-4 sm:p-8 bg-transparent sidebar-scroll transition-colors duration-500">
                    <div className="max-w-2xl mx-auto space-y-8">
                        <div className="relative bg-white/70 dark:bg-slate-900/60 backdrop-blur-3xl rounded-[2.5rem] border border-white/60 dark:border-white/10 shadow-xl shadow-slate-200/50 dark:shadow-black/50 overflow-hidden group hover:shadow-2xl hover:shadow-violet-500/10 transition-all duration-500">
                            <div className="p-8 sm:p-10 border-b border-white/60 dark:border-white/5 bg-white/40 dark:bg-slate-800/20 relative overflow-hidden transition-colors">
                                <div className="absolute -right-10 -top-10 w-32 h-32 bg-violet-50 dark:bg-violet-500/5 rounded-full opacity-40 group-hover:scale-150 transition-transform duration-700" />
                                <div className="flex flex-col sm:flex-row items-center gap-8 relative z-10">
                                    <div className="w-24 h-24 rounded-3xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center text-white text-4xl font-black shadow-2xl shadow-violet-500/30 transform group-hover:rotate-3 transition-transform">
                                        {user?.name?.charAt(0)}
                                    </div>
                                    <div className="text-center sm:text-left">
                                        <h3 className="text-2xl font-black text-slate-900 dark:text-white uppercase tracking-tight">{user?.name}</h3>
                                        <p className="text-sm text-slate-400 dark:text-slate-500 font-bold uppercase tracking-widest">{user?.email}</p>
                                        <div className="mt-4 flex flex-wrap justify-center sm:justify-start gap-2">
                                            <span className="px-4 py-1.5 rounded-full bg-violet-50 dark:bg-violet-500/10 text-violet-600 dark:text-violet-400 text-[10px] font-black uppercase tracking-widest border border-violet-100 dark:border-violet-500/20 shadow-sm">
                                                Passenger Assistant
                                            </span>
                                            {isVerified ? (
                                                <span className="px-4 py-1.5 rounded-full bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 text-[10px] font-black uppercase tracking-widest border border-emerald-100 dark:border-emerald-500/20 shadow-sm">
                                                    Verified
                                                </span>
                                            ) : (
                                                <span className="px-4 py-1.5 rounded-full bg-amber-50 dark:bg-amber-500/10 text-amber-600 dark:text-amber-400 text-[10px] font-black uppercase tracking-widest border border-amber-100 dark:border-amber-500/20 shadow-sm">
                                                    Pending
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <form onSubmit={handleSubmit} className="p-8 sm:p-10 space-y-8">
                                {message.text && (
                                    <div className={`p-5 rounded-2xl text-xs font-black uppercase tracking-widest border ${message.type === 'success' ? 'bg-emerald-50 dark:bg-emerald-500/10 border-emerald-100 dark:border-emerald-500/20 text-emerald-700 dark:text-emerald-400' : 'bg-red-50 dark:bg-red-500/10 border-red-100 dark:border-red-500/20 text-red-700 dark:text-red-400'}`}>
                                        {message.text}
                                    </div>
                                )}

                                <div className="grid gap-8">
                                    <div className="space-y-3">
                                        <label className="ml-1 uppercase tracking-[0.2em] text-[10px] font-black text-slate-400 flex items-center gap-2">
                                            <span className="w-1 h-1 rounded-full bg-violet-500" /> Display Name
                                        </label>
                                        <input name="name" type="text" value={formData.name} onChange={handleChange} required
                                            className="w-full px-6 py-4 rounded-2xl bg-white/80 dark:bg-slate-800/50 border-2 border-white/80 dark:border-white/10 focus:border-violet-500 dark:focus:border-violet-400 focus:ring-4 focus:ring-violet-500/5 outline-none transition-all text-sm font-black text-slate-700 dark:text-white shadow-sm" />
                                    </div>

                                    <div className="space-y-3">
                                        <label className="ml-1 uppercase tracking-[0.2em] text-[10px] font-black text-slate-400 flex items-center gap-2">
                                            <span className="w-1 h-1 rounded-full bg-slate-200" /> Email Address
                                        </label>
                                        <input value={user?.email} disabled
                                            className="w-full px-6 py-4 rounded-2xl bg-slate-50/80 dark:bg-slate-800/30 border-2 border-slate-100 dark:border-white/5 text-slate-400 dark:text-slate-500 cursor-not-allowed text-sm font-black shadow-inner" />
                                    </div>

                                    <div className="space-y-3">
                                        <label className="ml-1 uppercase tracking-[0.2em] text-[10px] font-black text-slate-400 flex items-center gap-2">
                                            <span className="w-1 h-1 rounded-full bg-violet-500" /> Mobile Number
                                        </label>
                                        <input name="phone" type="tel" value={formData.phone} onChange={handleChange} required
                                            className="w-full px-6 py-4 rounded-2xl bg-white/80 dark:bg-slate-800/50 border-2 border-white/80 dark:border-white/10 focus:border-violet-500 dark:focus:border-violet-400 focus:ring-4 focus:ring-violet-500/5 outline-none transition-all text-sm font-black text-slate-700 dark:text-white shadow-sm" />
                                    </div>
                                </div>

                                <button type="submit" disabled={loading}
                                    className="w-full py-5 rounded-2xl bg-slate-900 dark:bg-white/10 hover:bg-violet-600 dark:hover:bg-violet-500/30 text-white text-xs font-black uppercase tracking-[0.2em] shadow-xl shadow-slate-900/10 hover:shadow-violet-600/20 transition-all transform active:scale-[0.98] disabled:opacity-50">
                                    {loading ? 'SYNCING DATA...' : 'UPDATE PROFILE →'}
                                </button>
                            </form>
                        </div>

                        {/* Change Password Section */}
                        <div className="relative bg-white/70 dark:bg-slate-900/60 backdrop-blur-3xl rounded-[2.5rem] border border-white/60 dark:border-white/10 shadow-xl shadow-slate-200/50 dark:shadow-black/50 overflow-hidden group hover:shadow-2xl hover:shadow-violet-500/10 transition-all duration-500">
                            <div className="p-8 sm:p-10 border-b border-white/60 dark:border-white/5 bg-white/40 dark:bg-slate-800/20">
                                <h3 className="text-xl font-black text-slate-900 dark:text-white uppercase tracking-tight">Security Settings</h3>
                                <p className="text-sm text-slate-400 dark:text-slate-500 font-bold mt-1">Update your account password</p>
                            </div>
                            <form onSubmit={handlePasswordSubmit} className="p-8 sm:p-10 space-y-8">
                                {pwdMessage.text && (
                                    <div className={`p-5 rounded-2xl text-xs font-black uppercase tracking-widest border animate-in fade-in slide-in-from-top-4 duration-300 ${pwdMessage.type === 'success' ? 'bg-emerald-50 dark:bg-emerald-500/10 border-emerald-100 dark:border-emerald-500/20 text-emerald-700 dark:text-emerald-400' : 'bg-red-50 dark:bg-red-500/10 border-red-100 dark:border-red-500/20 text-red-700 dark:text-red-400'}`}>
                                        <div className="flex items-center gap-3">
                                            <span className="text-xl">{pwdMessage.type === 'success' ? <svg className="w-5 h-5 inline-block" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg> : <svg className="w-5 h-5 inline-block" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" /></svg>}</span>
                                            {pwdMessage.text}
                                        </div>
                                    </div>
                                )}
                                
                                <div className="grid gap-6">
                                    <div className="space-y-3">
                                        <label className="ml-1 uppercase tracking-[0.2em] text-[10px] font-black text-slate-400 flex items-center gap-2">
                                            Current Password
                                        </label>
                                        <div className="relative">
                                            <input
                                                name="currentPassword" type={showPwd ? 'text' : 'password'} value={passwordData.currentPassword} onChange={handlePasswordChange} required
                                                className="w-full px-6 py-4 rounded-2xl bg-white/80 dark:bg-slate-800/50 border-2 border-white/80 dark:border-white/10 focus:border-violet-500 dark:focus:border-violet-400 focus:ring-4 focus:ring-violet-500/5 outline-none transition-all text-sm font-black text-slate-700 dark:text-white shadow-sm"
                                                placeholder="Enter current password"
                                            />
                                            <button type="button" onClick={() => setShowPwd(!showPwd)} className="absolute inset-y-0 right-0 pr-5 flex items-center text-[10px] font-black text-slate-400 uppercase hover:text-violet-500 tracking-widest">
                                                {showPwd ? 'Hide' : 'Show'}
                                            </button>
                                        </div>
                                    </div>

                                    <div className="space-y-3">
                                        <label className="ml-1 uppercase tracking-[0.2em] text-[10px] font-black text-slate-400 flex items-center gap-2">
                                            New Password
                                        </label>
                                        <input
                                            name="newPassword" type={showPwd ? 'text' : 'password'} value={passwordData.newPassword} onChange={handlePasswordChange} required minLength={8}
                                            className="w-full px-6 py-4 rounded-2xl bg-white/80 dark:bg-slate-800/50 border-2 border-white/80 dark:border-white/10 focus:border-violet-500 dark:focus:border-violet-400 focus:ring-4 focus:ring-violet-500/5 outline-none transition-all text-sm font-black text-slate-700 dark:text-white shadow-sm"
                                            placeholder="••••••••"
                                        />
                                    </div>

                                    <div className="space-y-3">
                                        <label className="ml-1 uppercase tracking-[0.2em] text-[10px] font-black text-slate-400 flex items-center gap-2">
                                            Confirm New Password
                                        </label>
                                        <input
                                            name="confirmPassword" type={showPwd ? 'text' : 'password'} value={passwordData.confirmPassword} onChange={handlePasswordChange} required minLength={8}
                                            className="w-full px-6 py-4 rounded-2xl bg-white/80 dark:bg-slate-800/50 border-2 border-white/80 dark:border-white/10 focus:border-violet-500 dark:focus:border-violet-400 focus:ring-4 focus:ring-violet-500/5 outline-none transition-all text-sm font-black text-slate-700 dark:text-white shadow-sm"
                                            placeholder="••••••••"
                                        />
                                    </div>
                                </div>

                                <button type="submit" disabled={pwdLoading}
                                    className="w-full py-5 rounded-2xl bg-slate-900 dark:bg-white/10 hover:bg-violet-600 dark:hover:bg-violet-500/30 text-white text-xs font-black uppercase tracking-[0.2em] shadow-xl shadow-slate-900/10 hover:shadow-violet-600/20 transition-all transform active:scale-[0.98] disabled:opacity-50"
                                >
                                    {pwdLoading ? 'UPDATING...' : 'CHANGE PASSWORD →'}
                                </button>
                            </form>
                        </div>

                        {/* Logout */}
                        <div className="p-8 relative bg-slate-100/80 dark:bg-slate-900/40 backdrop-blur-xl rounded-[2.5rem] border border-slate-200 dark:border-white/5 flex flex-col sm:flex-row items-center justify-between gap-6">
                            <div className="text-center sm:text-left">
                                <h4 className="text-slate-900 dark:text-white font-black text-sm uppercase tracking-tight mb-1">Sign Out</h4>
                                <p className="text-slate-500 dark:text-slate-400 text-[10px] font-black uppercase tracking-widest opacity-80">Log out of your PA portal</p>
                            </div>
                            <button onClick={handleLogout}
                                className="w-full sm:w-auto px-10 py-3.5 rounded-2xl border-2 border-slate-300 dark:border-white/10 bg-white dark:bg-slate-800/30 text-slate-700 dark:text-slate-300 text-[10px] font-black uppercase tracking-widest hover:bg-slate-900 hover:text-white hover:border-slate-900 dark:hover:bg-white/10 transition-all shadow-sm">
                                SIGN OUT
                            </button>
                        </div>
                    </div>
                </main>
            </div>
            <BottomNav items={paMenuItems} />
        </div>
    );
};

export default PASettings;
